import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Upload,
  Popconfirm,
  Image,
  Progress,
  Alert,
  Tabs,
  Divider,
  Tag
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  CloudUploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const BASE_URL = 'https://ds2.cn/ds';

const LEVEL_OPTIONS = [
  { value: 'UR', label: 'UR', color: '#ff6b35' },
  { value: 'SSR', label: 'SSR', color: '#ffa500' },
  { value: 'SR', label: 'SR', color: '#9b59b6' },
  { value: 'R', label: 'R', color: '#3498db' }
] as const;

const getLevelColor = (level: 'UR' | 'SSR' | 'SR' | 'R') => {
  return LEVEL_OPTIONS.find(option => option.value === level)?.color || '#3498db';
};

interface StarCard {
  id: number;
  chineseName: string;
  englishName: string;
  hash: string;
  mainImage: string;
  description?: string;
  level: 'UR' | 'SSR' | 'SR' | 'R';
  visitCount: number;
  gravity?: number;
  background?: string;
  createdAt: string;
  updatedAt: string;
}

interface BatchUploadItem {
  chineseName: string;
  englishName: string;
  mainImage?: string;
  description?: string;
  level: 'UR' | 'SSR' | 'SR' | 'R';
  gravity?: number;
  background?: string;
  status?: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const StarCardAdmin: React.FC = () => {
  const [starCards, setStarCards] = useState<StarCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCard, setEditingCard] = useState<StarCard | null>(null);
  const [form] = Form.useForm();

  // 批量上传相关状态
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchData, setBatchData] = useState<BatchUploadItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<{ success: number; failed: number; total: number }>({ success: 0, failed: 0, total: 0 });

  // 批量更新相关状态
  const [batchUpdateModalVisible, setBatchUpdateModalVisible] = useState(false);
  const [updateData, setUpdateData] = useState<Array<{
    id: number;
    chineseName: string;
    englishName: string;
    level: 'UR' | 'SSR' | 'SR' | 'R';
    description?: string;
    mainImage?: string;
    gravity?: number;
    background?: string;
    status?: 'pending' | 'updating' | 'success' | 'error';
    error?: string;
  }>>([]);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateResults, setUpdateResults] = useState<{ success: number; failed: number; total: number }>({ success: 0, failed: 0, total: 0 });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '中文名',
      dataIndex: 'chineseName',
      key: 'chineseName',
      width: 120,
    },
    {
      title: '英文名',
      dataIndex: 'englishName',
      key: 'englishName',
      width: 120,
    },
    {
      title: 'Hash',
      dataIndex: 'hash',
      key: 'hash',
      width: 80,
      render: (hash: string) => (
        <Text code copyable>{hash}</Text>
      ),
    },
    {
      title: '主图',
      dataIndex: 'mainImage',
      key: 'mainImage',
      width: 100,
      render: (mainImage: string) => (
        mainImage ? <Image src={mainImage} width={50} height={50} /> : '-'
      ),
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level: 'UR' | 'SSR' | 'SR' | 'R') => (
        <Tag color={getLevelColor(level)} style={{ color: 'white', fontWeight: 'bold' }}>
          {level}
        </Tag>
      ),
      sorter: (a: StarCard, b: StarCard) => {
        const levelOrder = { 'UR': 4, 'SSR': 3, 'SR': 2, 'R': 1 };
        return levelOrder[a.level] - levelOrder[b.level];
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 180,
      render: (description: string) => (
        description ? <Text ellipsis style={{ maxWidth: 160 }}>{description}</Text> : '-'
      ),
    },
    {
      title: '重力系数',
      dataIndex: 'gravity',
      key: 'gravity',
      width: 100,
      render: (gravity: number) => (
        <Text>{gravity || 1.0}x</Text>
      ),
      sorter: (a: StarCard, b: StarCard) => (a.gravity || 1.0) - (b.gravity || 1.0),
    },
    {
      title: '背景图片',
      dataIndex: 'background',
      key: 'background',
      width: 120,
      render: (background: string) => (
        background ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Image
              src={background}
              width={40}
              height={30}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              placeholder={
                <div style={{
                  width: 40,
                  height: 30,
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 4,
                  fontSize: 12,
                  color: '#999'
                }}>
                  BG
                </div>
              }
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            />
            <Text ellipsis style={{ maxWidth: 60 }}>预览</Text>
          </div>
        ) : '-'
      ),
    },
    {
      title: '访问次数',
      dataIndex: 'visitCount',
      key: 'visitCount',
      width: 100,
      sorter: (a: StarCard, b: StarCard) => a.visitCount - b.visitCount,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_: any, record: StarCard) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(`${BASE_URL}/star-card#${record.hash}`, '_blank')}
          >
            查看
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这张星球卡吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchStarCards = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/star-card');
      const data = await response.json();
      if (data.success) {
        setStarCards(data.data);
      } else {
        message.error(data.message);
      }
    } catch (error) {
      message.error('获取星球卡列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCard(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (card: StarCard) => {
    setEditingCard(card);
    form.setFieldsValue({
      chineseName: card.chineseName,
      englishName: card.englishName,
      level: card.level,
      mainImage: card.mainImage,
      description: card.description,
      gravity: card.gravity || 1.0,
      background: card.background,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`/api/v1/star-card/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        message.success('删除成功');
        fetchStarCards();
      } else {
        message.error(data.message);
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const url = editingCard
        ? `/api/v1/star-card/${editingCard.id}`
        : '/api/v1/star-card';
      const method = editingCard ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();
      if (data.success) {
        message.success(editingCard ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchStarCards();
      } else {
        message.error(data.message);
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  // 批量上传功能
  const downloadTemplate = () => {
    const templateData = [
      ['中文名', '英文名', '等级', '主图URL', '描述', '重力系数', '背景图片'],
      ['火星', 'Mars', 'UR', 'https://example.com/mars.jpg', '红色的神秘星球，充满未知的可能', '0.38', 'https://example.com/mars-bg.jpg'],
      ['金星', 'Venus', 'SSR', 'https://example.com/venus.jpg', '爱与美的象征，炽热而迷人', '0.9', 'https://example.com/venus-bg.jpg'],
      ['地球', 'Earth', 'SR', 'https://example.com/earth.jpg', '我们的家园，孕育着无数生命', '1.0', 'https://example.com/earth-bg.jpg'],
      ['月球', 'Moon', 'R', 'https://example.com/moon.jpg', '地球的忠实伴侣，夜空中的明灯', '0.17', 'https://example.com/moon-bg.jpg'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, '星球卡批量上传模板.xlsx');
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        if (jsonData.length < 2) {
          message.error('文件内容不能为空');
          return;
        }

        const headers = jsonData[0];
        const chineseNameIndex = headers.findIndex(h => h === '中文名');
        const englishNameIndex = headers.findIndex(h => h === '英文名');
        const levelIndex = headers.findIndex(h => h === '等级');
        const mainImageIndex = headers.findIndex(h => h === '主图URL');
        const descriptionIndex = headers.findIndex(h => h === '描述');
        const gravityIndex = headers.findIndex(h => h === '重力系数');
        const backgroundIndex = headers.findIndex(h => h === '背景图片');

        if (chineseNameIndex === -1 || englishNameIndex === -1) {
          message.error('文件格式错误，请确保包含"中文名"和"英文名"列');
          return;
        }

        const parsedData: BatchUploadItem[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row[chineseNameIndex] && row[englishNameIndex]) {
            const level = levelIndex !== -1 ? String(row[levelIndex] || '').trim() : 'R';
            const validLevel = ['UR', 'SSR', 'SR', 'R'].includes(level) ? level as 'UR' | 'SSR' | 'SR' | 'R' : 'R';

            const gravityValue = gravityIndex !== -1 ? parseFloat(String(row[gravityIndex] || '1.0')) : 1.0;
            const validGravity = !isNaN(gravityValue) ? gravityValue : 1.0;

            parsedData.push({
              chineseName: String(row[chineseNameIndex]).trim(),
              englishName: String(row[englishNameIndex]).trim(),
              level: validLevel,
              mainImage: mainImageIndex !== -1 ? String(row[mainImageIndex] || '').trim() : '',
              description: descriptionIndex !== -1 ? String(row[descriptionIndex] || '').trim() : '',
              gravity: validGravity,
              background: backgroundIndex !== -1 ? String(row[backgroundIndex] || '').trim() : '',
              status: 'pending'
            });
          }
        }

        if (parsedData.length === 0) {
          message.error('未找到有效数据');
          return;
        }

        setBatchData(parsedData);
        setBatchModalVisible(true);
        message.success(`成功解析 ${parsedData.length} 条数据`);
      } catch (error) {
        console.error('文件解析错误:', error);
        message.error('文件解析失败，请检查文件格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止默认上传行为
  };

  const validateBatchData = (data: BatchUploadItem[]): { valid: BatchUploadItem[]; invalid: BatchUploadItem[] } => {
    const valid: BatchUploadItem[] = [];
    const invalid: BatchUploadItem[] = [];

    data.forEach(item => {
      if (!item.chineseName || !item.englishName) {
        invalid.push({ ...item, error: '中文名和英文名不能为空' });
      } else if (item.chineseName.length > 50 || item.englishName.length > 50) {
        invalid.push({ ...item, error: '名称长度不能超过50个字符' });
      } else {
        valid.push(item);
      }
    });

    return { valid, invalid };
  };

  const handleBatchUpload = async () => {
    const { valid, invalid } = validateBatchData(batchData);

    if (invalid.length > 0) {
      message.error(`有 ${invalid.length} 条数据验证失败，请检查数据格式`);
      setBatchData([...valid, ...invalid]);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResults({ success: 0, failed: 0, total: valid.length });

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < valid.length; i++) {
      const item = valid[i];

      // 更新当前项状态
      setBatchData(prev => prev.map((data, index) =>
        index === i ? { ...data, status: 'uploading' } : data
      ));

      try {
        const response = await fetch('/api/v1/star-card', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chineseName: item.chineseName,
            englishName: item.englishName,
            level: item.level,
            mainImage: item.mainImage || '',
            description: item.description || '',
            gravity: item.gravity || 1.0,
            background: item.background || ''
          }),
        });

        const result = await response.json();

        if (result.success) {
          successCount++;
          setBatchData(prev => prev.map((data, index) =>
            index === i ? { ...data, status: 'success' } : data
          ));
        } else {
          failedCount++;
          setBatchData(prev => prev.map((data, index) =>
            index === i ? { ...data, status: 'error', error: result.message || '创建失败' } : data
          ));
        }
      } catch (error) {
        failedCount++;
        setBatchData(prev => prev.map((data, index) =>
          index === i ? { ...data, status: 'error', error: '网络错误' } : data
        ));
      }

      // 更新进度
      const progress = Math.round(((i + 1) / valid.length) * 100);
      setUploadProgress(progress);
      setUploadResults({ success: successCount, failed: failedCount, total: valid.length });

      // 添加延迟避免请求过快
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setUploading(false);
    message.success(`批量上传完成！成功：${successCount}，失败：${failedCount}`);

    if (successCount > 0) {
      fetchStarCards(); // 刷新列表
    }
  };

  const closeBatchModal = () => {
    setBatchModalVisible(false);
    setBatchData([]);
    setUploadProgress(0);
    setUploadResults({ success: 0, failed: 0, total: 0 });
  };

  // 下载批量更新模板
  const downloadUpdateTemplate = async () => {
    if (starCards.length === 0) {
      message.warning('暂无数据，无法生成更新模板');
      return;
    }

    try {
      // 准备更新模板数据 - 包含ID、当前数据和可编辑字段
      const templateData = [
        // 表头
        ['ID', '中文名', '英文名', '等级', '描述', '主图URL', '重力系数', '背景图片', '说明'],
        // 说明行
        ['必填', '可修改', '可修改', '可修改(UR/SSR/SR/R)', '可修改', '可修改', '可修改(数字)', '可修改', '请勿修改ID列，其他列可以修改'],
        // 数据行
        ...starCards.map(card => [
          card.id,
          card.chineseName,
          card.englishName,
          card.level,
          card.description || '',
          card.mainImage || '',
          card.gravity || 1.0,
          card.background || '',
          `Hash: ${card.hash}, 访问次数: ${card.visitCount}`
        ])
      ];

      // 创建工作簿
      const ws = XLSX.utils.aoa_to_sheet(templateData);

      // 设置列宽
      const colWidths = [
        { wch: 8 },   // ID
        { wch: 20 },  // 中文名
        { wch: 20 },  // 英文名
        { wch: 10 },  // 等级
        { wch: 30 },  // 描述
        { wch: 40 },  // 主图URL
        { wch: 12 },  // 重力系数
        { wch: 25 },  // 背景图片
        { wch: 40 }   // 说明
      ];
      ws['!cols'] = colWidths;

      // 设置表头样式
      const headerStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "FFE0E0E0" } },
        alignment: { horizontal: "center" }
      };

      // 设置说明行样式
      const noteStyle = {
        font: { italic: true },
        fill: { fgColor: { rgb: "FFFFF2CC" } },
        alignment: { horizontal: "center" }
      };

      // 应用样式
      for (let i = 0; i < 9; i++) {
        const headerCell = XLSX.utils.encode_cell({ r: 0, c: i });
        const noteCell = XLSX.utils.encode_cell({ r: 1, c: i });
        if (!ws[headerCell]) ws[headerCell] = {};
        if (!ws[noteCell]) ws[noteCell] = {};
        ws[headerCell].s = headerStyle;
        ws[noteCell].s = noteStyle;
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '批量更新模板');

      // 生成文件名
      const fileName = `星球卡批量更新模板_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;

      // 下载文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(data, fileName);

      message.success(`成功下载批量更新模板，包含 ${starCards.length} 条数据`);
    } catch (error) {
      console.error('下载更新模板失败:', error);
      message.error('下载更新模板失败，请重试');
    }
  };

  // 导出Excel功能
  const exportToExcel = async () => {
    try {
      setLoading(true);

      // 如果当前数据为空，重新获取
      let dataToExport = starCards;
      if (dataToExport.length === 0) {
        const response = await fetch('/api/v1/star-card');
        const result = await response.json();
        if (result.success) {
          dataToExport = result.data;
        } else {
          throw new Error(result.message || '获取数据失败');
        }
      }

      // 准备导出数据
      const exportData = [
        // 表头
        ['ID', '中文名', '英文名', '等级', 'Hash', 'URL', '主图URL', '描述', '重力系数', '背景图片', '主图提示词', '背景图提示词', '访问次数', '创建时间', '更新时间'],
        // 数据行
        ...dataToExport.map(card => [
          card.id,
          card.chineseName,
          card.englishName,
          card.level,
          card.hash,
          `https://ds2.cn/sc/${card.hash}`,
          card.mainImage || '',
          card.description || '',
          card.gravity || 1.0,
          card.background || '',
          `为这个星球："${card.chineseName}"生成一张透明背景的图片。不要添加任何文字。`,
          `生成一张卡通图，模拟处在这个星球：“${card.chineseName}”表面的样子。包含地平线、天空。地平线的位置保持在70%（从上往下），地平线需要水平，地平线以下是土壤剖面图。用于角色走路。要求能够还原这个星球的地貌、天空、天气等。用于作为游戏背景，不要包含人物。`,
          card.visitCount,
          dayjs(card.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          dayjs(card.updatedAt).format('YYYY-MM-DD HH:mm:ss')
        ])
      ];

      // 创建工作簿
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // 设置列宽
      const colWidths = [
        { wch: 8 },   // ID
        { wch: 15 },  // 中文名
        { wch: 15 },  // 英文名
        { wch: 8 },   // 等级
        { wch: 8 },   // Hash
        { wch: 35 },  // URL
        { wch: 40 },  // 主图URL
        { wch: 30 },  // 描述
        { wch: 12 },  // 重力系数
        { wch: 40 },  // 背景图片
        { wch: 50 },  // 主图提示词
        { wch: 80 },  // 背景图提示词
        { wch: 10 },  // 访问次数
        { wch: 20 },  // 创建时间
        { wch: 20 }   // 更新时间
      ];
      ws['!cols'] = colWidths;

      // 设置表头样式
      const headerStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "FFE0E0E0" } },
        alignment: { horizontal: "center" }
      };

      // 应用表头样式
      for (let i = 0; i < 15; i++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: i });
        if (!ws[cellAddress]) ws[cellAddress] = {};
        ws[cellAddress].s = headerStyle;
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '星球卡数据');

      // 生成文件名（包含当前时间）
      const fileName = `星球卡数据_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;

      // 下载文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(data, fileName);

      message.success(`成功导出 ${dataToExport.length} 条数据`);
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 批量更新Excel文件处理
  const handleUpdateFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        if (jsonData.length < 3) {
          message.error('文件内容不正确，请使用正确的批量更新模板');
          return;
        }

        const headers = jsonData[0];
        const idIndex = headers.findIndex(h => h === 'ID');
        const chineseNameIndex = headers.findIndex(h => h === '中文名');
        const englishNameIndex = headers.findIndex(h => h === '英文名');
        const levelIndex = headers.findIndex(h => h === '等级');
        const descriptionIndex = headers.findIndex(h => h === '描述');
        const mainImageIndex = headers.findIndex(h => h === '主图URL');
        const gravityIndex = headers.findIndex(h => h === '重力系数');
        const backgroundIndex = headers.findIndex(h => h === '背景图片');

        if (idIndex === -1 || chineseNameIndex === -1 || englishNameIndex === -1) {
          message.error('文件格式错误，请确保包含"ID"、"中文名"和"英文名"列');
          return;
        }

        const parsedData: Array<{
          id: number;
          chineseName: string;
          englishName: string;
          level: 'UR' | 'SSR' | 'SR' | 'R';
          description?: string;
          mainImage?: string;
          gravity?: number;
          background?: string;
          status: 'pending';
        }> = [];

        // 从第3行开始解析数据（跳过表头和说明行）
        for (let i = 2; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row[idIndex] && row[chineseNameIndex] && row[englishNameIndex]) {
            const id = parseInt(String(row[idIndex]));
            if (isNaN(id)) {
              continue; // 跳过无效ID
            }

            const level = levelIndex !== -1 ? String(row[levelIndex] || '').trim() : 'R';
            const validLevel = ['UR', 'SSR', 'SR', 'R'].includes(level) ? level as 'UR' | 'SSR' | 'SR' | 'R' : 'R';

            const gravityValue = gravityIndex !== -1 ? parseFloat(String(row[gravityIndex] || '1.0')) : 1.0;
            const validGravity = !isNaN(gravityValue) ? gravityValue : 1.0;

            parsedData.push({
              id,
              chineseName: String(row[chineseNameIndex]).trim(),
              englishName: String(row[englishNameIndex]).trim(),
              level: validLevel,
              description: descriptionIndex !== -1 ? String(row[descriptionIndex] || '').trim() : '',
              mainImage: mainImageIndex !== -1 ? String(row[mainImageIndex] || '').trim() : '',
              gravity: validGravity,
              background: backgroundIndex !== -1 ? String(row[backgroundIndex] || '').trim() : '',
              status: 'pending'
            });
          }
        }

        if (parsedData.length === 0) {
          message.error('未找到有效的更新数据');
          return;
        }

        // 验证ID是否存在于当前数据中
        const existingIds = starCards.map(card => card.id);
        const validData = parsedData.filter(item => existingIds.includes(item.id));
        const invalidData = parsedData.filter(item => !existingIds.includes(item.id));

        if (invalidData.length > 0) {
          message.warning(`发现 ${invalidData.length} 条数据的ID不存在，将被忽略`);
        }

        if (validData.length === 0) {
          message.error('没有找到可以更新的有效数据');
          return;
        }

        setUpdateData(validData);
        setBatchUpdateModalVisible(true);
        message.success(`成功解析 ${validData.length} 条更新数据`);
      } catch (error) {
        console.error('文件解析错误:', error);
        message.error('文件解析失败，请检查文件格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止默认上传行为
  };

  // 执行批量更新
  const handleBatchUpdate = async () => {
    if (updateData.length === 0) {
      message.warning('没有数据需要更新');
      return;
    }

    setUploading(true);
    setUpdateProgress(0);
    setUpdateResults({ success: 0, failed: 0, total: updateData.length });

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < updateData.length; i++) {
      const item = updateData[i];

      // 更新当前项状态
      setUpdateData(prev => prev.map((data, index) =>
        index === i ? { ...data, status: 'updating' } : data
      ));

      try {
        const response = await fetch(`/api/v1/star-card/${item.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chineseName: item.chineseName,
            englishName: item.englishName,
            level: item.level,
            description: item.description || '',
            mainImage: item.mainImage || '',
            gravity: item.gravity || 1.0,
            background: item.background || ''
          }),
        });

        const result = await response.json();

        if (result.success) {
          successCount++;
          setUpdateData(prev => prev.map((data, index) =>
            index === i ? { ...data, status: 'success' } : data
          ));
        } else {
          failedCount++;
          setUpdateData(prev => prev.map((data, index) =>
            index === i ? { ...data, status: 'error', error: result.message || '更新失败' } : data
          ));
        }
      } catch (error) {
        failedCount++;
        setUpdateData(prev => prev.map((data, index) =>
          index === i ? { ...data, status: 'error', error: '网络错误' } : data
        ));
      }

      // 更新进度
      const progress = Math.round(((i + 1) / updateData.length) * 100);
      setUpdateProgress(progress);
      setUpdateResults({ success: successCount, failed: failedCount, total: updateData.length });

      // 添加延迟避免请求过快
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setUploading(false);
    message.success(`批量更新完成！成功：${successCount}，失败：${failedCount}`);

    if (successCount > 0) {
      fetchStarCards(); // 刷新列表
    }
  };

  // 关闭批量更新模态框
  const closeBatchUpdateModal = () => {
    setBatchUpdateModalVisible(false);
    setUpdateData([]);
    setUpdateProgress(0);
    setUpdateResults({ success: 0, failed: 0, total: 0 });
  };

  useEffect(() => {
    fetchStarCards();
  }, []);

  return (
    <div className="star-card-admin">
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>星球卡管理</Title>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                添加星球卡
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setBatchModalVisible(true)}
              >
                批量上传
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                loading={loading}
              >
                导出Excel
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadUpdateTemplate}
                loading={loading}
              >
                下载更新模板
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() => setBatchUpdateModalVisible(true)}
              >
                批量更新
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchStarCards}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={starCards}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />

        <Modal
          title={editingCard ? '编辑星球卡' : '添加星球卡'}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            name="starCardForm"
          >
            <Form.Item
              name="chineseName"
              label="中文名"
              rules={[{ required: true, message: '请输入中文名' }]}
            >
              <Input placeholder="请输入星球中文名" />
            </Form.Item>

            <Form.Item
              name="englishName"
              label="英文名"
              rules={[{ required: true, message: '请输入英文名' }]}
            >
              <Input placeholder="请输入星球英文名" />
            </Form.Item>

            <Form.Item
              name="level"
              label="等级"
              rules={[{ required: true, message: '请选择等级' }]}
              initialValue="R"
            >
              <Select placeholder="请选择等级">
                {LEVEL_OPTIONS.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    <Tag color={option.color} style={{ color: 'white', fontWeight: 'bold', margin: 0 }}>
                      {option.label}
                    </Tag>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="mainImage"
              label="主图URL"
            >
              <Input placeholder="请输入主图URL" />
            </Form.Item>

            {editingCard && (
              <Form.Item
                label="上传图片"
              >
                <Upload
                  name="image"
                  listType="picture-card"
                  className="avatar-uploader"
                  showUploadList={false}
                  action={`/api/v1/star-card/${editingCard.hash}/upload-image`}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('只能上传图片文件！');
                      return false;
                    }
                    const isLt10M = file.size / 1024 / 1024 < 10;
                    if (!isLt10M) {
                      message.error('图片大小必须小于10MB！');
                      return false;
                    }
                    return true;
                  }}
                  onChange={(info) => {
                    if (info.file.status === 'uploading') {
                      message.loading({ content: '正在上传...', key: 'upload' });
                    }
                    if (info.file.status === 'done') {
                      message.destroy('upload');
                      if (info.file.response?.success) {
                        message.success('图片上传成功');
                        // 更新表单中的主图URL
                        form.setFieldsValue({
                          mainImage: info.file.response.data.url
                        });
                        // 刷新星球卡列表
                        fetchStarCards();
                      } else {
                        message.error(info.file.response?.message || '上传失败');
                      }
                    }
                    if (info.file.status === 'error') {
                      message.destroy('upload');
                      message.error('上传失败');
                    }
                  }}
                >
                  <div>
                    <CloudUploadOutlined />
                    <div style={{ marginTop: 8 }}>点击上传图片</div>
                  </div>
                </Upload>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  支持 JPG、PNG、WebP 格式，文件大小不超过10MB
                </Text>
              </Form.Item>
            )}

            <Form.Item
              name="description"
              label="描述"
            >
              <Input.TextArea rows={3} placeholder="请输入星球描述" />
            </Form.Item>

            <Form.Item
              name="gravity"
              label="重力系数"
              initialValue={1.0}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const numValue = Number(value);
                    if (isNaN(numValue)) {
                      return Promise.reject(new Error('请输入有效的数字'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input
                type="number"
                step="0.1"
                placeholder="请输入重力系数（相对于地球，默认1.0）"
                addonAfter="x 地球重力"
              />
            </Form.Item>

            <Form.Item
              name="background"
              label="背景图片URL"
            >
              <Input placeholder="请输入背景图片URL" />
            </Form.Item>

            {editingCard && (
              <Form.Item
                label="上传背景图片"
              >
                <Upload
                  name="background"
                  listType="picture-card"
                  className="avatar-uploader"
                  showUploadList={false}
                  action={`/api/v1/star-card/${editingCard.hash}/upload-background`}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('只能上传图片文件！');
                      return false;
                    }
                    const isLt10M = file.size / 1024 / 1024 < 10;
                    if (!isLt10M) {
                      message.error('图片大小必须小于10MB！');
                      return false;
                    }
                    return true;
                  }}
                  onChange={(info) => {
                    if (info.file.status === 'uploading') {
                      message.loading({ content: '正在上传背景图片...', key: 'upload-bg' });
                    }
                    if (info.file.status === 'done') {
                      message.destroy('upload-bg');
                      if (info.file.response?.success) {
                        message.success('背景图片上传成功');
                        // 更新表单中的背景图片URL
                        form.setFieldsValue({
                          background: info.file.response.data.url
                        });
                        // 刷新星球卡列表
                        fetchStarCards();
                      } else {
                        message.error(info.file.response?.message || '背景图片上传失败');
                      }
                    }
                    if (info.file.status === 'error') {
                      message.destroy('upload-bg');
                      message.error('背景图片上传失败');
                    }
                  }}
                >
                  <div>
                    <CloudUploadOutlined />
                    <div style={{ marginTop: 8 }}>点击上传背景图片</div>
                  </div>
                </Upload>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  支持 JPG、PNG、WebP 格式，文件大小不超过10MB，用于ExplorePage游戏背景
                </Text>
              </Form.Item>
            )}

            {editingCard && (
              <Form.Item label="Hash">
                <Input value={editingCard.hash} disabled />
              </Form.Item>
            )}

            {!editingCard && (
              <Form.Item>
                <Text type="secondary">Hash将自动生成（4位）</Text>
              </Form.Item>
            )}
          </Form>
        </Modal>

        {/* 批量上传Modal */}
        <Modal
          title="批量上传星球卡"
          open={batchModalVisible}
          onCancel={closeBatchModal}
          width={900}
          footer={null}
        >
          <Tabs defaultActiveKey="1">
            <TabPane tab="文件上传" key="1">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message="使用说明"
                  description={
                    <div>
                      <p>1. 请先下载模板文件，按照格式填写数据</p>
                      <p>2. 必填字段：中文名、英文名</p>
                      <p>3. 可选字段：等级（UR/SSR/SR/R，默认R）、主图URL、描述</p>
                      <p>4. 支持 Excel (.xlsx) 格式文件</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Row gutter={16}>
                  <Col span={12}>
                    <Button
                      type="dashed"
                      icon={<DownloadOutlined />}
                      onClick={downloadTemplate}
                      block
                    >
                      下载模板文件
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Upload
                      accept=".xlsx,.xls"
                      beforeUpload={handleFileUpload}
                      showUploadList={false}
                    >
                      <Button
                        icon={<FileExcelOutlined />}
                        block
                      >
                        选择Excel文件
                      </Button>
                    </Upload>
                  </Col>
                </Row>
              </Space>
            </TabPane>

            <TabPane tab="数据预览" key="2" disabled={batchData.length === 0}>
              {batchData.length > 0 && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text strong>共 {batchData.length} 条数据</Text>
                    </Col>
                    <Col>
                      <Space>
                        <Button
                          type="primary"
                          loading={uploading}
                          onClick={handleBatchUpload}
                          disabled={batchData.length === 0}
                        >
                          开始上传
                        </Button>
                        <Button onClick={closeBatchModal}>
                          取消
                        </Button>
                      </Space>
                    </Col>
                  </Row>

                  {uploading && (
                    <div style={{ marginBottom: 16 }}>
                      <Text>上传进度：</Text>
                      <Progress percent={uploadProgress} />
                      <Text type="secondary">
                        成功：{uploadResults.success} | 失败：{uploadResults.failed} | 总计：{uploadResults.total}
                      </Text>
                    </div>
                  )}

                  <Table
                    dataSource={batchData}
                    pagination={false}
                    scroll={{ y: 400 }}
                    size="small"
                    rowKey={(record, index) => index!}
                    rowClassName={(record) => {
                      switch (record.status) {
                        case 'success': return 'batch-upload-success';
                        case 'error': return 'batch-upload-error';
                        case 'uploading': return 'batch-upload-uploading';
                        default: return '';
                      }
                    }}
                    columns={[
                      {
                        title: '状态',
                        dataIndex: 'status',
                        width: 80,
                        render: (status) => {
                          switch (status) {
                            case 'pending': return <Text type="secondary">待上传</Text>;
                            case 'uploading': return <Text type="warning">上传中...</Text>;
                            case 'success': return <Text type="success">成功</Text>;
                            case 'error': return <Text type="danger">失败</Text>;
                            default: return <Text type="secondary">待上传</Text>;
                          }
                        }
                      },
                      {
                        title: '中文名',
                        dataIndex: 'chineseName',
                        width: 120,
                      },
                      {
                        title: '英文名',
                        dataIndex: 'englishName',
                        width: 120,
                      },
                      {
                        title: '等级',
                        dataIndex: 'level',
                        width: 80,
                        render: (level: 'UR' | 'SSR' | 'SR' | 'R') => (
                          <Tag color={getLevelColor(level)} style={{ color: 'white', fontWeight: 'bold' }}>
                            {level}
                          </Tag>
                        ),
                      },
                      {
                        title: '主图URL',
                        dataIndex: 'mainImage',
                        width: 200,
                        render: (url) => url ? <Text ellipsis style={{ maxWidth: 180 }}>{url}</Text> : '-'
                      },
                      {
                        title: '描述',
                        dataIndex: 'description',
                        width: 200,
                        render: (description) => description ? <Text ellipsis style={{ maxWidth: 180 }}>{description}</Text> : '-'
                      },
                      {
                        title: '错误信息',
                        dataIndex: 'error',
                        render: (error) => error ? <Text type="danger">{error}</Text> : '-'
                      }
                    ]}
                  />
                </Space>
              )}
            </TabPane>
          </Tabs>
        </Modal>

        {/* 批量更新模态框 */}
        <Modal
          title="批量更新星球卡"
          open={batchUpdateModalVisible}
          onCancel={() => setBatchUpdateModalVisible(false)}
          width={900}
          footer={null}
        >
          <Tabs defaultActiveKey="1">
            <TabPane tab="文件上传" key="1">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message="使用说明"
                  description={
                    <div>
                      <p>1. 请先下载更新模板文件，模板包含现有数据</p>
                      <p>2. 根据ID字段匹配现有记录进行更新</p>
                      <p>3. 可更新字段：中文名、英文名、等级、主图URL、描述</p>
                      <p>4. ID字段不可修改，用于匹配现有记录</p>
                      <p>5. 支持 Excel (.xlsx) 格式文件</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Row gutter={16}>
                  <Col span={12}>
                    <Button
                      type="dashed"
                      icon={<DownloadOutlined />}
                      onClick={downloadUpdateTemplate}
                      loading={loading}
                      block
                    >
                      下载更新模板
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Upload
                      accept=".xlsx,.xls"
                      beforeUpload={handleUpdateFileUpload}
                      showUploadList={false}
                    >
                      <Button
                        icon={<FileExcelOutlined />}
                        block
                      >
                        选择Excel文件
                      </Button>
                    </Upload>
                  </Col>
                </Row>
              </Space>
            </TabPane>

            <TabPane tab="数据预览" key="2" disabled={updateData.length === 0}>
              {updateData.length > 0 && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text strong>共 {updateData.length} 条更新数据</Text>
                    </Col>
                    <Col>
                      <Space>
                        <Button
                          type="primary"
                          loading={uploading}
                          onClick={handleBatchUpdate}
                          disabled={updateData.length === 0}
                        >
                          开始更新
                        </Button>
                        <Button onClick={() => setBatchUpdateModalVisible(false)}>
                          取消
                        </Button>
                      </Space>
                    </Col>
                  </Row>

                  {uploading && (
                    <div style={{ marginBottom: 16 }}>
                      <Text>更新进度：</Text>
                      <Progress percent={uploadProgress} />
                      <Text type="secondary">
                        成功：{uploadResults.success} | 失败：{uploadResults.failed} | 总计：{uploadResults.total}
                      </Text>
                    </div>
                  )}

                  <Table
                    dataSource={updateData}
                    pagination={false}
                    scroll={{ y: 400 }}
                    size="small"
                    rowKey="id"
                    rowClassName={(record) => {
                      switch (record.status) {
                        case 'success': return 'batch-upload-success';
                        case 'error': return 'batch-upload-error';
                        case 'updating': return 'batch-upload-uploading';
                        default: return '';
                      }
                    }}
                    columns={[
                      {
                        title: '状态',
                        dataIndex: 'status',
                        width: 80,
                        render: (status) => {
                          switch (status) {
                            case 'pending': return <Text type="secondary">待更新</Text>;
                            case 'updating': return <Text type="warning">更新中...</Text>;
                            case 'success': return <Text type="success">成功</Text>;
                            case 'error': return <Text type="danger">失败</Text>;
                            default: return <Text type="secondary">待更新</Text>;
                          }
                        }
                      },
                      {
                        title: 'ID',
                        dataIndex: 'id',
                        width: 60,
                      },
                      {
                        title: '中文名',
                        dataIndex: 'chineseName',
                        width: 120,
                      },
                      {
                        title: '英文名',
                        dataIndex: 'englishName',
                        width: 120,
                      },
                      {
                        title: '等级',
                        dataIndex: 'level',
                        width: 80,
                        render: (level: 'UR' | 'SSR' | 'SR' | 'R') => (
                          <Tag color={getLevelColor(level)} style={{ color: 'white', fontWeight: 'bold' }}>
                            {level}
                          </Tag>
                        ),
                      },
                      {
                        title: '主图URL',
                        dataIndex: 'mainImage',
                        width: 180,
                        render: (url) => url ? <Text ellipsis style={{ maxWidth: 160 }}>{url}</Text> : '-'
                      },
                      {
                        title: '描述',
                        dataIndex: 'description',
                        width: 180,
                        render: (description) => description ? <Text ellipsis style={{ maxWidth: 160 }}>{description}</Text> : '-'
                      },
                      {
                        title: '错误信息',
                        dataIndex: 'error',
                        render: (error) => error ? <Text type="danger">{error}</Text> : '-'
                      }
                    ]}
                  />
                </Space>
              )}
            </TabPane>
          </Tabs>
        </Modal>
      </Card>
    </div>
  );
};

export default StarCardAdmin;